import { useEffect, useRef } from 'react';
import { areBackgroundAnimationsPaused } from 'src/utils/background-animations';

const DOT_SPACING = 6;
const DOT_MIN_SIZE = 0.8;
const DOT_MAX_SIZE = 3.2;
const CORE_RADIUS_X = 220;
const CORE_RADIUS_Y = 30;
const TRAVEL_DURATION = 7826;
const BRIGHTNESS = 1.04;

export function PredictiveArc() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    let width = 0;
    let height = 0;
    let frame = 0;

    function resize() {
      if (!canvas) {
        return;
      }

      const ratio = Math.min(window.devicePixelRatio || 1, 2);

      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      context?.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    function draw(time: number) {
      if (!context) {
        return;
      }

      const progress = (time % TRAVEL_DURATION) / TRAVEL_DURATION;
      const coreX = (-0.2 + progress * 1.4) * width;
      const coreY = height / 2;
      const pulse = 0.85 + 0.15 * Math.sin((time / 1000) * 2.07);

      context.clearRect(0, 0, width, height);

      for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
        for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
          const dx = (x - coreX) / (CORE_RADIUS_X * pulse);
          const dy = (y - coreY) / CORE_RADIUS_Y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const glow = Math.max(0, 1 - distance);
          const intensity = glow * glow;

          const red = Math.min(255, (2 + intensity * 148) * BRIGHTNESS);
          const green = Math.min(255, (40 + intensity * 165) * BRIGHTNESS);
          const blue = Math.min(255, (110 + intensity * 145) * BRIGHTNESS);
          const alpha = Math.min(1, (0.22 + intensity * 0.78) * BRIGHTNESS);

          const breath =
            0.7 + 0.3 * Math.sin((time / 1000) * 2.4 + (x + y) * 0.06);
          const size =
            (DOT_MIN_SIZE + intensity * (DOT_MAX_SIZE - DOT_MIN_SIZE)) * breath;

          context.fillStyle = `rgba(${red}, ${green}, ${blue}, ${alpha})`;
          context.fillRect(x - size / 2, y - size / 2, size, size);
        }
      }
    }

    function loop(time: number) {
      if (!areBackgroundAnimationsPaused()) draw(time);
      frame = window.requestAnimationFrame(loop);
    }

    resize();

    if (prefersReducedMotion) {
      draw(TRAVEL_DURATION * 0.5);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
